import type { ExternalTableSchema } from '@packages/table-builder';

type TableColumn = NonNullable<ExternalTableSchema['columns']>[number];

/** Shared vesting-list / included / detail filter bar — keep in sync across all release item tables. */
export const releaseItemFilters: NonNullable<
  NonNullable<ExternalTableSchema['dataSource']>['filters']
> = [
    {
      name: 'createdFromDate',
      placeholder: 'Vesting Date From',
      type: 'date',
    },
    {
      name: 'createdToDate',
      placeholder: 'Vesting Date To',
      type: 'date',
    },
    {
      name: 'awardTypeId',
      placeholder: 'Award Type',
      type: 'asyncSelect',
      dataSource: {
        url: '/api/award/api/app/plan-type-category/release-type-category-list',
        labelField: 'name',
        valueField: 'id',
      },
    },
    {
      name: 'grantName',
      placeholder: 'Grant Name',
      type: 'asyncSelect',
      dataSource: {
        url: '/api/award/api/app/grant/grants',
        labelField: 'name',
        valueField: 'id',
      },
    },
    {
      name: 'settlementTypeIds',
      placeholder: 'Settlement Type',
      type: 'asyncSelect',
      dataSource: {
        url: '/api/award/api/app/option-exercise/settlement-types',
        labelField: 'displayName',
        valueField: 'id',
      },
    },
    {
      name: 'CountryIds',
      placeholder: 'Country',
      type: 'asyncSelect',
      multiple: true,
      dataSource: {
        url: '/api/customer/api/app/country/countries',
        labelField: 'name',
        valueField: 'id',
      },
    },
    {
      name: 'IsTaxCertificationVerified',
      placeholder: 'Tax Certification',
      type: 'select',
      options: [
        { label: 'Certified', value: true },
        { label: 'Uncertified', value: false },
      ],
    },
    {
      name: 'BrokerAccountStatusIds',
      placeholder: 'Broker Account Status',
      type: 'asyncSelect',
      multiple: true,
      dataSource: {
        url: '/api/customer/api/app/participant-broker-account/broker-status-list',
        labelField: 'displayName',
        valueField: 'id',
      },
    },
  ];

/** Award APIs use SearchKeyWord — never table-builder default Filter. */
export const releaseItemSearchRequestParams = {
  SearchKeyWord: 'table.searchText',
  Filter: '',
  MaxResultCount: 'table.pageSize',
  SkipCount: 'table.pageOffset',
  Sorting: 'table.sorting',
  GrantId: 'filters.grantName',
  AwardTypeId: 'filters.awardTypeId',
  FromDate: 'filters.createdFromDate',
  ToDate: 'filters.createdToDate',
  SettlementTypeIds: 'filters.settlementTypeIds',
  CountryIds: 'filters.CountryIds',
  IsTaxCertificationVerified: 'filters.IsTaxCertificationVerified',
  BrokerAccountStatusIds: 'filters.BrokerAccountStatusIds',
} as const;

/** Legacy null money → '-' (never emptyDisplay: '0'). */
export const releaseFmvColumn: TableColumn = {
  field: 'fmv',
  header: 'FMV $',
  type: 'text',
  sortable: true,
  width: 180,
  align: 'center',
  format: { decimals: 4, fixed: true },
  fields: { formatAsNumber: 'true' },
};

export const releaseVestingValuesColumn: TableColumn = {
  field: 'vestingValues',
  header: 'Vesting Value $',
  type: 'text',
  sortable: true,
  width: 200,
  align: 'center',
  format: { decimals: 4, fixed: true },
  fields: { formatAsNumber: 'true' },
};

/** Legacy isNumberField — comma integer in Tag/Lozenge default (gray badge). */
export const releaseVestingUnitColumn: TableColumn = {
  field: 'vestingUnit',
  header: 'Unit',
  type: 'number',
  sortable: true,
  width: 180,
  align: 'center',
  format: { commas: true },
};

const releaseParticipantDetailColumn: TableColumn = {
  field: 'participantDetail',
  header: 'Participant Details',
  sortable: true,
  width: 250,
};

/** Backend: add `country` to release vesting / included / detail list DTOs. */
const releaseCountryColumn: TableColumn = {
  field: 'country',
  header: 'Country',
  type: 'text',
  sortable: false,
  width: 200,
};

const releaseVestDateColumn: TableColumn = {
  field: 'vestingDate',
  header: 'Vest Date',
  type: 'date',
  sortable: true,
  width: 200,
};

const releaseGrantNameColumn: TableColumn = {
  field: 'grantName',
  header: 'Grant Name',
  sortable: true,
  width: 180,
};

export const releaseAwardNumberColumn: TableColumn = {
  field: 'awardNumber',
  header: 'Award ID',
  sortable: true,
  width: 180,
};

export const releaseAwardIdColumn: TableColumn = {
  field: 'awardId',
  header: 'Award ID',
  sortable: true,
  width: 180,
};

const releaseAwardTypeColumn: TableColumn = {
  field: 'awardType',
  header: 'Award Type',
  type: 'badge',
  sortable: true,
  width: 200,
  align: 'center',
  badge: {
    statusMap: { RSU: 'success', PSU: 'pink-tag' },
    defaultVariant: 'default',
  },
};

const releaseTaxCertColumn: TableColumn = {
  field: 'isTaxCertificationVerified',
  header: 'Tax Certification',
  type: 'custom',
  sortable: false,
  width: 250,
  align: 'center',
  renderer: 'releaseTaxCertification',
};

const releaseSettlementTypeColumn: TableColumn = {
  field: 'settlementType',
  header: 'Settlement Type',
  sortable: true,
  width: 180,
};

/** Backend: add `brokerAccountStatusId` to release vesting / included list DTOs. */
const releaseBrokerAccountColumn: TableColumn = {
  field: 'brokerAccountStatusId',
  header: 'Broker Account Status',
  type: 'badge',
  sortable: false,
  width: 180,
  align: 'center',
  badge: {
    statusMap: { 1: 'info', 2: 'info', 3: 'success', 4: 'success', 5: 'error', 6: 'info' },
    defaultVariant: 'default',
  },
  conditionalValues: {
    1: 'DRAFT',
    2: 'PENDING BROKER',
    3: 'BROKER RECEIVED',
    4: 'BROKER CREATED',
    5: 'BROKER REJECTED',
    6: 'NOT REQUIRED',
  },
};

const releaseAccountNumberColumn: TableColumn = {
  field: 'accountNumber',
  header: 'Account Number',
  sortable: false,
  width: 180,
};

const releaseTaxCollectionColumn: TableColumn = {
  field: 'taxCollection',
  header: 'Tax Collection',
  type: 'badge',
  sortable: true,
  width: 250,
  align: 'center',
  badge: {
    statusMap: { Pending: 'info', Completed: 'success', Reversed: 'primary' },
  },
  conditionalValues: {
    Pending: 'PENDING',
    Completed: 'COMPLETED',
    Reversed: 'REVERSED',
  },
};

/** Included tab column order (legacy releaseForm included tab). */
export const releaseIncludedItemColumns: TableColumn[] = [
  releaseParticipantDetailColumn,
  releaseCountryColumn,
  releaseVestDateColumn,
  releaseVestingValuesColumn,
  releaseFmvColumn,
  releaseVestingUnitColumn,
  releaseGrantNameColumn,
  releaseAwardNumberColumn,
  releaseAwardTypeColumn,
  releaseTaxCertColumn,
  releaseSettlementTypeColumn,
  releaseBrokerAccountColumn,
  releaseAccountNumberColumn,
];

/** Select-items tab — awardId accessor (vesting-list DTO). */
export const releaseSelectedItemColumns: TableColumn[] = [
  releaseParticipantDetailColumn,
  releaseCountryColumn,
  releaseVestDateColumn,
  releaseVestingValuesColumn,
  releaseFmvColumn,
  releaseVestingUnitColumn,
  releaseGrantNameColumn,
  releaseAwardIdColumn,
  releaseAwardTypeColumn,
  releaseTaxCertColumn,
  releaseSettlementTypeColumn,
  releaseBrokerAccountColumn,
  releaseAccountNumberColumn,
];

/** Release Details tab (view page) — broker/account columns aligned with select/included item lists. */
export const releaseDetailsItemColumns: TableColumn[] = [
  releaseParticipantDetailColumn,
  releaseCountryColumn,
  { ...releaseFmvColumn, width: 200 },
  releaseVestingValuesColumn,
  releaseVestingUnitColumn,
  releaseGrantNameColumn,
  releaseAwardNumberColumn,
  releaseAwardTypeColumn,
  releaseVestDateColumn,
  releaseTaxCertColumn,
  releaseTaxCollectionColumn,
  releaseSettlementTypeColumn,
  releaseBrokerAccountColumn,
  releaseAccountNumberColumn,
];

export function withReleaseIdEndpoint(
  schema: ExternalTableSchema,
  releaseId: string,
): ExternalTableSchema {
  return {
    ...schema,
    dataSource: schema.dataSource
      ? {
        ...schema.dataSource,
        endpoint: schema.dataSource.endpoint.replace('{id}', releaseId),
      }
      : schema.dataSource,
  };
}
